<template>
    <div class="CriterionTextInput">
        <FormElement :label="hideTitle ? null : form.schema.title">
            <template v-if="form.schema.description" #labelHint>
                <div
                    v-tooltip="form.schema.description"
                    class="CriterionEnumInput__hintIcon"
                >
                    <FontAwesomeIcon :icon="faQuestionCircle" />
                </div>
            </template>
            <FormInput :invalid="showValidationErrors && (showError || accessed) && hasError" force-error-slot>
                <input
                    type="text"
                    :value="value"
                    :maxlength="form.schema.maxLength || 1024"
                    :disabled="rbacDisabled"
                    @input="onInput"
                    @blur="touch"
                    @focus="onFocus"
                    @change="onChange"
                >
                <template v-if="showValidationErrors && (showError || accessed) && error" #errorMessage>
                    {{ error }}
                </template>
                <template v-if="deletable || rbacDisabled" #actionButtons>
                    <LockInputIcon v-if="rbacDisabled" />
                    <NewButton
                        v-else-if="deletable"
                        variant="flat"
                        size="icon-sm"
                        color="secondary"
                        class="JsonSchemaForm__deleteIcon"
                        @click="$emit('delete')"
                    >
                        <slot name="deleteIcon">
                            <EidIcon :icon-src="faXmark" alt="delete" />
                        </slot>
                    </NewButton>
                </template>
            </FormInput>
        </FormElement>
    </div>
</template>

<script lang="ts">
    import { Component } from '@evidentid/vue-property-decorator';

    import trim from 'lodash/trim';

    import { faQuestionCircle } from '@fortawesome/free-regular-svg-icons';
    import { faXmark } from '@fortawesome/pro-light-svg-icons';
    import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import { FormElement, FormInput } from '@evidentid/dashboard-commons/components/Form';
    import LockInputIcon from '@evidentid/dashboard-commons/components/LockInputIcon/LockInputIcon.vue';
    import { JsonFormBase } from '@evidentid/json-schema/interfaces/JsonForm';
    import { JsonSchemaString } from '@evidentid/json-schema/interfaces/JsonSchema';

    import NewButton from '@/components/new-button/NewButton.vue';
    import AbstractCriterionInput
        from '@/modules/decisioning-criteria/components/CriterionInputComponents/AbstractCriterionInput';

    @Component({
        emits: [ 'change', 'delete', 'input' ],
        components: { EidIcon, FormInput, FontAwesomeIcon, FormElement, LockInputIcon, NewButton },
    })
    export default class CriterionTextInput extends AbstractCriterionInput<JsonFormBase<JsonSchemaString>, string> {
        private faXmark = faXmark;
        private faQuestionCircle = faQuestionCircle;

        protected override get empty(): boolean {
            return this.form.isEmpty(this.form.getValue(trim(this.value)));
        }

        protected override get valid(): boolean {
            if (!this.required && this.empty) {
                return true;
            }
            return this.form.isValid(this.form.getValue(trim(this.value)));
        }

        protected override get error(): string | null {
            if (this.empty) {
                return this.required
                    ? this.translate('error.required')
                    : null;
            }
            return this.valid ? null : this.getCustomError() || this.translate('error.unknown');
        }

        private onInput(event: InputEvent) {
            const inputValue = (event.target as HTMLInputElement).value;
            this.$emit('input', this.form.getValue(inputValue));
        }

        private onChange(event: Event): void {
            const inputValue = (event.target as HTMLInputElement).value;
            this.$emit('change', this.form.getValue(inputValue));
        }
    }
</script>
